import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "@/config/api";
import type { AnnotationInput, AnnotationSaveResponse } from "@/hooks/useLabelingQueries";
import { VerdictNotSentError } from "@/predictions/verdictError";
import { fetchWithRetry } from "@/hooks/studyMetadataRetry";
import {
  MAX_REQUEST_RETRIES,
  isFinalRequestError,
  isTransientRequestError,
  requestRetryTiming,
  saveAttemptTimeoutMs,
  transientQueryRetry,
} from "@/lib/requestRetry";

export type CandidateVerdict = "accepted" | "rejected" | "pending";
export interface CandidateVerdictEvent {
  event_id: string;
  condition: string;
  source_segment_index: number;
  verdict: CandidateVerdict;
  annotation_id: string | null;
  annotation_snapshot?: AnnotationSaveResponse | null;
}
interface VerdictResponse {
  verdicts: CandidateVerdictEvent[];
}
interface Decision {
  condition: string;
  source_segment_index: number;
  verdict: CandidateVerdict;
  annotation?: AnnotationInput;
}
interface Command extends Decision {
  event_id: string;
  expected_event_id: string | null;
}
const EMPTY_VERDICTS: CandidateVerdictEvent[] = [];

export function candidateVerdictKey(condition: string, sourceSegmentIndex: number): string {
  return JSON.stringify([condition, sourceSegmentIndex]);
}

/** The server refused it (4xx other than 408/429): reload and unlock. Anything else stays unconfirmed. */
const isDefinitiveFailure = isFinalRequestError;

/**
 * A card whose save the server confirmed, while decisions reload. It stays
 * locked until the loaded decisions no longer hold `staleEventId`, its event
 * from before the save (ENG-229).
 */
interface Settling {
  owner: object;
  key: string;
  staleEventId: string | null;
}

/** A decision save in flight, for the status line (ENG-229). */
export interface SaveProgress {
  startedAt: number;
  attempt: number;
  maxAttempts: number;
}

function useAnnotationConfirmation(scope: string) {
  const confirmation = useMemo(() => {
    let resolve: ((event: CandidateVerdictEvent) => void) | undefined;
    let reject: ((error: unknown) => void) | undefined;
    return {
      start: () => {
        if (resolve) throw new Error("An annotation save is already awaiting confirmation");
        return new Promise<CandidateVerdictEvent>((done, fail) => {
          resolve = done;
          reject = fail;
        });
      },
      resolve: (event: CandidateVerdictEvent) => {
        resolve?.(event);
        resolve = undefined;
        reject = undefined;
      },
      reject: (error: unknown) => {
        reject?.(error);
        resolve = undefined;
        reject = undefined;
      },
    };
  }, [scope]);
  useEffect(
    () => () => confirmation.reject(new Error("Study changed before save was confirmed")),
    [confirmation],
  );
  return confirmation;
}

function annotationResult(event: CandidateVerdictEvent): AnnotationSaveResponse {
  const saved = event.annotation_snapshot;
  if (!saved) throw new Error("Saved annotation is missing from the decision response");
  return {
    annotation_id: saved.annotation_id,
    segment_index: saved.segment_index,
    segmentation_stats: saved.segmentation_stats,
    hull_stats: saved.hull_stats ?? undefined,
  };
}

function useDecisionWriter(
  scope: string,
  url: string,
  reload: () => Promise<unknown>,
  /** After the reload: record the server's answer if the decisions still show the old event. */
  applyConfirmed: (command: Command, event: CandidateVerdictEvent) => void,
) {
  // A new owner also distinguishes returning to a case from its earlier visit.
  const owner = useMemo(() => ({}), [scope]);
  const activeOwner = useRef(owner);
  activeOwner.current = owner;
  const busy = useRef(new Set<object>());
  // Slowest successful save on this page; raises later attempts' timeouts.
  const slowestSuccessMs = useRef(0);
  const [state, setState] = useState<{
    owner: object;
    command: Command;
    saving: boolean;
    progress: SaveProgress | null;
  } | null>(null);
  const [settling, setSettling] = useState<Settling[]>([]);
  const write = useCallback(
    async (command: Command) => {
      if (busy.current.has(owner)) {
        throw new VerdictNotSentError("decision-busy", "A decision is already being saved");
      }
      busy.current.add(owner);
      // Set once the server answers: from then on, another card may start its save.
      let released = false;
      const progress = { startedAt: Date.now(), attempt: 1, maxAttempts: MAX_REQUEST_RETRIES + 1 };
      setState({ owner, command, saving: true, progress });
      try {
        // Every attempt resends this same command, so the backend replays a
        // repeated event_id instead of saving twice.
        const { data } = await fetchWithRetry(
          async (attempt) => {
            if (activeOwner.current === owner) {
              setState((current) => current && { ...current, progress: { ...progress, attempt } });
            }
            const started = Date.now();
            const response = await axios.post<CandidateVerdictEvent>(url, command, {
              timeout: saveAttemptTimeoutMs(attempt, slowestSuccessMs.current),
            });
            slowestSuccessMs.current = Math.max(slowestSuccessMs.current, Date.now() - started);
            return response;
          },
          {
            maxAttempts: MAX_REQUEST_RETRIES + 1,
            isRetryable: isTransientRequestError,
            delayMs: (attempt) => requestRetryTiming.delayMs(attempt),
            // Stop retrying once the reader has left this case.
            signal: {
              get aborted() {
                return activeOwner.current !== owner;
              },
            },
          },
        );
        // The server answered: unlock every other card now, and keep only this
        // one locked until the reload replaces its event (ENG-229).
        released = true;
        busy.current.delete(owner);
        if (activeOwner.current === owner) {
          setState(null);
          setSettling((current) => [
            ...current.filter((entry) => entry.owner === owner),
            {
              owner,
              key: candidateVerdictKey(command.condition, command.source_segment_index),
              staleEventId: command.expected_event_id,
            },
          ]);
        }
        await reload();
        // A reload that read slightly old data (or was cut short) must not leave
        // this card locked: the server's answer is the newest state we know.
        if (activeOwner.current === owner) applyConfirmed(command, data);
        return data;
      } catch (error) {
        if (released) throw error;
        if (isDefinitiveFailure(error)) {
          await reload();
          if (activeOwner.current === owner) setState(null);
        }
        throw error;
      } finally {
        if (!released) busy.current.delete(owner);
        if (!released && activeOwner.current === owner) {
          setState((current) => current && { ...current, saving: false, progress: null });
        }
      }
    },
    [url, reload, owner, applyConfirmed],
  );
  const forgetSettled = useCallback((done: readonly Settling[]) => {
    setSettling((current) => current.filter((entry) => !done.includes(entry)));
  }, []);
  const ownSettling = useMemo(
    () => settling.filter((entry) => entry.owner === owner),
    [settling, owner],
  );
  const current = state?.owner === owner ? state : null;
  return {
    write,
    pending: current?.command,
    isSaving: current?.saving ?? false,
    progress: current?.saving ? current.progress : null,
    settling: ownSettling,
    forgetSettled,
  };
}

function useCandidateAnnotationSave(
  isLocked: (condition: string, sourceSegmentIndex: number) => boolean,
  writer: ReturnType<typeof useDecisionWriter>,
  confirmation: ReturnType<typeof useAnnotationConfirmation>,
) {
  return useCallback(
    async (annotation: AnnotationInput, expected: string | null) => {
      const condition = String(annotation.attributes.ai_prediction_condition);
      const sourceSegmentIndex = Number(annotation.attributes.ai_prediction_source_segment_index);
      if (isLocked(condition, sourceSegmentIndex)) {
        throw new Error("Saved decisions must load before saving an annotation");
      }
      const { edited_annotation_id: _edited, ...payload } = annotation;
      const pending = confirmation.start();
      const command: Command = {
        condition,
        source_segment_index: sourceSegmentIndex,
        verdict: "accepted",
        annotation: payload,
        event_id: crypto.randomUUID(),
        expected_event_id: expected,
      };
      // Keep the editor's save pending through an unknown outcome. The visible
      // Retry decision action resends this exact command and settles the save.
      void writer.write(command).then(confirmation.resolve, (error) => {
        if (isDefinitiveFailure(error)) confirmation.reject(error);
      });
      return annotationResult(await pending);
    },
    [isLocked, confirmation, writer.write],
  );
}

export function useCandidateVerdicts(projectId: string, studyUid: string, enabled: boolean) {
  const queryClient = useQueryClient();
  const cleanStudyUid = studyUid.split("::")[0];
  const scope = JSON.stringify([projectId, cleanStudyUid]);
  const queryKey = ["candidate-verdicts", projectId, cleanStudyUid] as const;
  const url =
    `${getApiBaseUrl()}/api/labeling/projects/${encodeURIComponent(projectId)}` +
    `/studies/${encodeURIComponent(cleanStudyUid)}/candidate-verdicts`;
  const query = useQuery({
    queryKey,
    queryFn: async () => (await axios.get<VerdictResponse>(url)).data,
    enabled,
    ...transientQueryRetry,
    staleTime: 0,
    refetchOnMount: "always",
  });
  const reload = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["candidate-verdicts", projectId, cleanStudyUid] }),
      queryClient.invalidateQueries({ queryKey: ["annotations", projectId, cleanStudyUid] }),
      queryClient.invalidateQueries({ queryKey: ["annotation-stats", projectId] }),
    ]);
  }, [queryClient, projectId, cleanStudyUid]);
  const applyConfirmed = useCallback(
    (command: Command, event: CandidateVerdictEvent) => {
      const key = candidateVerdictKey(command.condition, command.source_segment_index);
      if (
        typeof event?.event_id !== "string" ||
        candidateVerdictKey(event.condition, event.source_segment_index) !== key
      ) {
        return;
      }
      const verdictsKey = ["candidate-verdicts", projectId, cleanStudyUid];
      // A failed reload keeps its alert and Retry; writing data would hide it.
      if (queryClient.getQueryState(verdictsKey)?.status === "error") return;
      queryClient.setQueryData<VerdictResponse>(verdictsKey, (current) => {
        if (!current) return current;
        const others = current.verdicts.filter(
          (item) => candidateVerdictKey(item.condition, item.source_segment_index) !== key,
        );
        const stored = current.verdicts.find(
          (item) => candidateVerdictKey(item.condition, item.source_segment_index) === key,
        );
        // Only replace the event from before the save, never a newer one.
        if ((stored?.event_id ?? null) !== command.expected_event_id) return current;
        return { ...current, verdicts: [...others, event] };
      });
    },
    [queryClient, projectId, cleanStudyUid],
  );
  const writer = useDecisionWriter(scope, url, reload, applyConfirmed);
  // The saved card's Edit needs its new annotation too, so it also waits for that reload.
  const annotationsReloading =
    useIsFetching({ queryKey: ["annotations", projectId, cleanStudyUid] }) > 0;
  const confirmation = useAnnotationConfirmation(scope);
  // Cards whose confirmed save the loaded decisions and annotations don't show yet.
  const settlingKeys = useMemo(() => {
    const eventIds = new Map(
      (query.data?.verdicts ?? []).map((event) => [
        candidateVerdictKey(event.condition, event.source_segment_index),
        event.event_id,
      ]),
    );
    return new Set(
      writer.settling
        .filter(
          ({ key, staleEventId }) =>
            annotationsReloading || (eventIds.get(key) ?? null) === staleEventId,
        )
        .map(({ key }) => key),
    );
  }, [query.data, writer.settling, annotationsReloading]);
  // Forget a card once its save shows in the decisions, so a later change to that
  // card (a delete, another tab) can't match its old event and lock it again.
  useEffect(() => {
    const done = writer.settling.filter(({ key }) => !settlingKeys.has(key));
    if (done.length > 0) writer.forgetSettled(done);
  }, [writer.settling, writer.forgetSettled, settlingKeys]);
  // Our own reload after a save keeps the other cards usable; a first load or a
  // Retry loading still locks every card.
  const ownReload = settlingKeys.size > 0;
  const readReady =
    enabled && query.isSuccess && !query.isError && (!query.isFetching || ownReload);
  const ready = readReady && !writer.isSaving && !writer.pending;
  const isCandidateLocked = useCallback(
    (condition: string, sourceSegmentIndex: number) =>
      !ready || settlingKeys.has(candidateVerdictKey(condition, sourceSegmentIndex)),
    [ready, settlingKeys],
  );
  const setVerdict = useCallback(
    async (decision: Decision) => {
      if (isCandidateLocked(decision.condition, decision.source_segment_index)) {
        throw new VerdictNotSentError(
          "decisions-not-ready",
          "Saved decisions must load before another decision",
        );
      }
      const previous = query.data?.verdicts.find(
        (event) =>
          event.condition === decision.condition &&
          event.source_segment_index === decision.source_segment_index,
      );
      await writer.write({
        ...decision,
        event_id: crypto.randomUUID(),
        expected_event_id: previous?.event_id ?? null,
      });
    },
    [isCandidateLocked, query.data, writer.write],
  );
  const retryDecision = useCallback(async () => {
    if (!readReady || !writer.pending) throw new Error("Reload saved decisions before retrying");
    try {
      const event = await writer.write(writer.pending);
      confirmation.resolve(event);
    } catch (error) {
      if (isDefinitiveFailure(error)) confirmation.reject(error);
      throw error;
    }
  }, [readReady, writer.pending, writer.write, confirmation]);
  const saveAnnotation = useCandidateAnnotationSave(isCandidateLocked, writer, confirmation);
  return {
    verdicts: query.data?.verdicts ?? EMPTY_VERDICTS,
    ready,
    isCandidateLocked,
    isLoading: query.isPending || (query.isFetching && !ownReload),
    error: query.error,
    isSaving: writer.isSaving,
    saveProgress: writer.progress,
    hasUnconfirmedDecision: !!writer.pending && !writer.isSaving,
    // Which candidate's decision is unconfirmed, so the rail can keep it in focus.
    unconfirmedCandidate:
      writer.pending && !writer.isSaving
        ? {
            condition: writer.pending.condition,
            source_segment_index: writer.pending.source_segment_index,
          }
        : null,
    retryDecision,
    reload,
    setVerdict,
    saveAnnotation,
  };
}
